import { useState } from "react";
import {
  Body1,
  Button,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Card } from "@fluentui/react-components/unstable";
import EngagementHistoryDialog from "./EngagementHistoryDialog";
import { formatResourceAllocationDuration } from "../../utils/resourceAllocationDuration";
import type { IMyEngagementApi } from "../../services/types/resourceAllocation";

interface EngagementStatusCardProps {
  engagement: IMyEngagementApi;
  isLoading: boolean;
  year: number;
  month: number;
}

const useStyles = makeStyles({
  cardContainer: {
    width: "100%",
    zIndex: "5",
    "@media (min-width: 966px)": {
      width: "50%",
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  total: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
    paddingBottom: "0.35rem",
    marginBottom: "0.5rem",
    fontWeight: tokens.fontWeightSemibold,
  },
  line: {
    display: "flex",
    justifyContent: "space-between",
    columnGap: "1rem",
    paddingBlock: "0.2rem",
  },
  lineMeta: {
    display: "flex",
    flexDirection: "column",
  },
  projectName: {
    fontWeight: tokens.fontWeightRegular,
  },
  managerName: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  lineHours: {
    whiteSpace: "nowrap",
    fontWeight: tokens.fontWeightSemibold,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    paddingBlock: "0.75rem",
  },
  historyButton: {
    float: "right",
    marginTop: "1rem",
    marginLeft: "1rem",
    "@media (max-width: 444px)": {
      marginBottom: "1rem",
    },
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    paddingBlock: "1.5rem",
  },
});

function formatMonthYear(year: number, month: number): string {
  return `${String(month).padStart(2, "0")}.${year}`;
}

const EngagementStatusCard: React.FC<EngagementStatusCardProps> = ({
  engagement,
  isLoading,
  year,
  month,
}) => {
  const classes = useStyles();
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <Card className={classes.cardContainer}>
      <div className={classes.cardHeader}>
        <h3>Twoje planowane zaangażowanie w {formatMonthYear(year, month)}</h3>
      </div>
      <Body1>
        {isLoading ? (
          <div className={classes.centered}>
            <Spinner label="Ładowanie..." />
          </div>
        ) : (
          <>
            <div className={classes.total}>
              <span>Zlecone godziny</span>
              <span>
                {formatResourceAllocationDuration(engagement.totalOrderedMinutes)}
              </span>
            </div>

            {engagement.lines.length === 0 ? (
              <div className={classes.empty}>
                Brak zleconych godzin w tym miesiącu.
              </div>
            ) : (
              engagement.lines.map((line, index) => (
                <div
                  key={`${line.youTrackProjectId}-${line.managerFullName}-${index}`}
                  className={classes.line}
                >
                  <span className={classes.lineMeta}>
                    <span className={classes.projectName}>
                      {line.youTrackProjectLabel}
                    </span>
                    <span className={classes.managerName}>
                      Kierownik: {line.managerFullName}
                    </span>
                  </span>
                  <span className={classes.lineHours}>
                    {formatResourceAllocationDuration(line.orderedMinutes)}
                  </span>
                </div>
              ))
            )}
          </>
        )}

        <Button
          className={classes.historyButton}
          appearance="primary"
          onClick={() => setHistoryOpen(true)}
        >
          Historia zaangażowania
        </Button>
      </Body1>

      <EngagementHistoryDialog
        open={historyOpen}
        setOpen={setHistoryOpen}
        initialYear={year}
        initialMonth={month}
      />
    </Card>
  );
};

export default EngagementStatusCard;
